/**
 * AlertSound - Sistema de sonido para alertas de precio
 * Usa Web Audio API para generar sonidos sin archivos externos
 */

// Contexto de audio global
let audioContext = null;

// Inicializar contexto de audio (debe llamarse tras interacción del usuario)
export const initAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Tipos de sonido disponibles
export const ALERT_SOUNDS = {
  success: { name: 'Éxito', frequency: 880, duration: 0.15, type: 'sine', repeat: 2 },
  warning: { name: 'Aviso', frequency: 440, duration: 0.2, type: 'square', repeat: 1 },
  danger: { name: 'Alerta', frequency: 220, duration: 0.3, type: 'sawtooth', repeat: 3 },
  notification: { name: 'Notificación', frequency: 660, duration: 0.1, type: 'sine', repeat: 2 },
  priceUp: { name: 'Precio Sube', frequency: [523, 659, 784], duration: 0.12, type: 'sine' },
  priceDown: { name: 'Precio Baja', frequency: [784, 659, 523], duration: 0.12, type: 'sine' },
};

// Reproducir un tono simple
const playTone = (ctx, frequency, duration, type = 'sine', startTime = 0) => {
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + startTime);
  
  // Envelope ADSR simple
  const now = ctx.currentTime + startTime;
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01); // Attack
  gainNode.gain.linearRampToValueAtTime(0.2, now + duration * 0.3); // Decay
  gainNode.gain.linearRampToValueAtTime(0.2, now + duration * 0.7); // Sustain
  gainNode.gain.linearRampToValueAtTime(0, now + duration); // Release
  
  oscillator.start(now);
  oscillator.stop(now + duration);
};

// Reproducir sonido de alerta
export const playAlertSound = (soundType = 'notification') => {
  try {
    const ctx = initAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    const sound = ALERT_SOUNDS[soundType] || ALERT_SOUNDS.notification;
    
    if (Array.isArray(sound.frequency)) {
      // Secuencia de tonos (para priceUp/priceDown)
      sound.frequency.forEach((freq, i) => {
        playTone(ctx, freq, sound.duration, sound.type, i * sound.duration);
      });
    } else if (sound.repeat && sound.repeat > 1) {
      // Tonos repetidos
      for (let i = 0; i < sound.repeat; i++) {
        playTone(ctx, sound.frequency, sound.duration, sound.type, i * (sound.duration + 0.05));
      }
    } else {
      // Tono simple
      playTone(ctx, sound.frequency, sound.duration, sound.type);
    }
  } catch (error) {
    logger.warn('Error playing alert sound:', error);
  }
};

// Hook para configuración de sonidos
import { useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';

export const useAlertSounds = () => {
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('alertSoundEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  const [selectedSound, setSelectedSound] = useState(() => {
    return localStorage.getItem('alertSoundType') || 'notification';
  });
  
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('alertSoundVolume');
    return saved !== null ? parseFloat(saved) : 0.5;
  });

  // Guardar configuración
  useEffect(() => {
    localStorage.setItem('alertSoundEnabled', JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  useEffect(() => {
    localStorage.setItem('alertSoundType', selectedSound);
  }, [selectedSound]);

  useEffect(() => {
    localStorage.setItem('alertSoundVolume', volume.toString());
  }, [volume]);

  // Reproducir sonido si está habilitado
  const playSound = useCallback((type) => {
    if (soundEnabled) {
      playAlertSound(type || selectedSound);
    }
  }, [soundEnabled, selectedSound]);

  // Probar sonido
  const testSound = useCallback(() => {
    playAlertSound(selectedSound);
  }, [selectedSound]);

  return {
    soundEnabled,
    setSoundEnabled,
    selectedSound,
    setSelectedSound,
    volume,
    setVolume,
    playSound,
    testSound
  };
};

export default playAlertSound;
